import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { api } from '../api/client'
import type { TrialExtensionStatus } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { LogoCompleto } from './Marchio'
import { t } from '../i18n/lingua'

/** L'evento con cui `request` segnala che l'accesso al tenant è interrotto. */
export const EVENTO_LICENZA_SCADUTA = 'censo:licenza-scaduta'

export interface DettaglioScadenza {
  trial?: boolean
  /** Il tenant non ha nessuna licenza assegnata. */
  missing?: boolean
  valid_until?: string
  message?: string
}

/**
 * La licenza del tenant è scaduta: al posto della console, una pagina che dice
 * perché e cosa fare.
 *
 * Il **login** resta libero — chi appartiene a più tenant sceglie qui un altro
 * tenant — ma ogni API di questo tenant risponde `license_expired`, e mostrare
 * quelle risposte come errori sparsi (una tabella vuota qui, un banner rosso là)
 * farebbe credere a un guasto. I dati restano: estendendo la licenza, tutto
 * torna com'era.
 */
export function LicenzaScaduta({ children }: { children: ReactNode }) {
  const { principal, switchTenant, logout } = useAuth()
  const [scaduta, setScaduta] = useState<DettaglioScadenza | null>(null)

  useEffect(() => {
    const ascolta = (e: Event) => setScaduta((e as CustomEvent<DettaglioScadenza>).detail ?? {})
    window.addEventListener(EVENTO_LICENZA_SCADUTA, ascolta)
    return () => window.removeEventListener(EVENTO_LICENZA_SCADUTA, ascolta)
  }, [])

  // Cambiando tenant, si riparte da capo.
  useEffect(() => setScaduta(null), [principal?.tenant_id])

  // L'estensione della prova: che cosa si può chiedere adesso.
  const [estensione, setEstensione] = useState<TrialExtensionStatus | null>(null)
  const [invio, setInvio] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    if (!scaduta?.trial) return
    api.trialExtension().then(setEstensione).catch(() => setEstensione(null))
  }, [scaduta])

  async function chiedi() {
    setInvio(true)
    setErrore(null)
    try {
      const esito = await api.requestTrialExtension()
      if (esito.last?.status === 'auto_approved' && !esito.pending) {
        // Approvata da sola: l'accesso è tornato, si ricarica la console.
        window.location.reload()
        return
      }
      setEstensione(esito)
    } catch (err) {
      setErrore(err instanceof Error ? err.message : String(err))
    } finally {
      setInvio(false)
    }
  }

  if (!scaduta) return <>{children}</>

  const altri = (principal?.tenants ?? []).filter((x) => x.tenant_id !== principal?.tenant_id)
  return (
    <div className="login">
      <div className="login__box" style={{ maxWidth: 520 }}>
        <div className="login__marchio"><LogoCompleto size={48} /></div>
        <h1 className="page__title" style={{ textAlign: 'center' }}>
          {scaduta.missing
            ? t('Nessuna licenza assegnata')
            : scaduta.trial
              ? t('Il periodo di prova è terminato')
              : t('La licenza è scaduta')}
        </h1>
        <p>
          {scaduta.missing
            ? t('A questo tenant non è assegnata una licenza: contatta il tuo fornitore.')
            : scaduta.trial
            ? t('Per continuare a usare CENSO contatta il tuo fornitore: i dati sono conservati e tornano disponibili appena la licenza viene estesa.')
            : t('Per continuare a usare CENSO contatta il tuo fornitore: i dati sono conservati e tornano disponibili appena la licenza viene rinnovata.')}
        </p>
        {scaduta.valid_until && (
          <p className="field__hint">
            {t('Scaduta il {data}.', { data: new Date(scaduta.valid_until).toLocaleDateString() })}
          </p>
        )}
        {scaduta.trial && estensione && (
          <div className="field">
            {estensione.pending ? (
              <p className="banner banner--info">
                {t('Richiesta di {n} giorni inviata il {data}: in attesa di approvazione del fornitore.', {
                  n: estensione.pending.days,
                  data: estensione.pending.requested_at ? new Date(estensione.pending.requested_at).toLocaleDateString() : '—',
                })}
              </p>
            ) : (
              <>
                {estensione.last?.status === 'rejected' && (
                  <p className="banner banner--warn">
                    {t('L’ultima richiesta di estensione non è stata approvata.')}
                    {estensione.retry_after && (
                      <>{' '}{t('Puoi farne un’altra dal {data}.', { data: new Date(estensione.retry_after).toLocaleString() })}</>
                    )}
                  </p>
                )}
                <button type="button" onClick={() => void chiedi()} disabled={invio || !estensione.can_request}>
                  {estensione.automatic
                    ? t('Estendi la prova di {n} giorni', { n: estensione.next_days })
                    : t('Richiedi altri {n} giorni', { n: estensione.next_days })}
                </button>
                <p className="field__hint">
                  {estensione.automatic
                    ? t('La prima estensione è immediata.')
                    : t('Le estensioni successive le approva il fornitore: riceverai l’accesso appena approvata.')}
                </p>
              </>
            )}
            {errore && <p className="banner banner--error">{errore}</p>}
          </div>
        )}
        {altri.length > 0 && (
          <div className="field">
            <label htmlFor="altro-tenant">{t('Vai a un altro tenant')}</label>
            <select
              id="altro-tenant"
              defaultValue=""
              onChange={(e) => e.target.value && void switchTenant(e.target.value)}
            >
              <option value="">{t('— scegli —')}</option>
              {altri.map((x) => (
                <option key={x.tenant_id} value={x.tenant_id}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <button type="button" onClick={logout}>
          {t('Esci')}
        </button>
      </div>
    </div>
  )
}
